import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaArrowRightLong } from "react-icons/fa6";
import { getSiteSettings } from "../../api/siteSettings";
import { getLibraryAppointmentSettings } from "../../api/libraryAppointmentService";
import "../../styles/components/_header.scss";
import Icon from "../Icons/Icon";
import {
  CaretDownIcon,
  FacebookIcon,
  InstagramIcon,
  SearchIcon,
  XIcon,
  YoutubeIcon,
} from "../Icons/SiteIcons";
import useWeather from "../../hooks/useWeather";

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [qrModeEnabled, setQrModeEnabled] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const temperature = useWeather();
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(82);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await getSiteSettings();
        if (response.isSuccess) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };

    const fetchLibrarySettings = async () => {
      try {
        const response = await getLibraryAppointmentSettings();
        if (response?.isSuccess && response?.data) {
          setQrModeEnabled(response.data.qrModeEnabled !== false);
        }
      } catch (error) {
        console.error("Error fetching library appointment settings:", error);
        setQrModeEnabled(true);
      }
    };

    fetchSiteSettings();
    fetchLibrarySettings();

    // Measure real header height for mobile menu positioning
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleDropdownClick = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
  };

  const handleLinkClick = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm("");
    }
  };

  const getWeatherIcon = (temp) => {
    if (!temp) return "CloudRain";
    const value = parseInt(temp, 10);

    if (value >= 30) return "sun";
    if (value >= 20) return "cloud";
    if (value >= 10) return "cloudSun";
    if (value >= 0) return "CloudRain";
    return "CloudSnow";
  };

  const eBelediyeUrl =
    siteSettings?.ePayUrl ||
    siteSettings?.eMunicipalityUrl ||
    "https://webportal.iskenderun.bel.tr/engine/index.php";

  return (
    <header className="header" ref={headerRef}>
      <div className="header-container">
        <div className="logo">
          <Link to="/" onClick={handleLinkClick}>
            <img
              src={siteSettings?.logoUrl || "/assets/images/logo.png"}
              alt="İskenderun Belediyesi"
            />
          </Link>
        </div>

        <button
          className={`hamburger ${isMobileMenuOpen ? "active" : ""}`}
          onClick={toggleMobileMenu}
          aria-label="Menüyü aç"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav
          className={`menu ${isMobileMenuOpen ? "active" : ""}`}
          style={window.innerWidth <= 768 ? { top: `${headerHeight}px` } : {}}
        >
          <ul className="menu-list">
            <li className="dropdown">
              <button
                onClick={() => handleDropdownClick("baskan")}
                className={activeDropdown === "baskan" ? "active" : ""}
              >
                Başkan <CaretDownIcon className="dropdown-icon" />
              </button>
              <ul className={`dropdown-menu ${activeDropdown === "baskan" ? "active" : ""}`}>
                <li><Link to="/baskan" onClick={handleLinkClick}>Başkan Özgeçmiş</Link></li>
                <li><Link to="/baskan-gorev-yetkileri" onClick={handleLinkClick}>Başkan Görev ve Yetkileri</Link></li>
                <li><Link to="/misyon-vizyon-ve-temel-degerler" onClick={handleLinkClick}>Misyon, Vizyon ve Temel Değerler</Link></li>
              </ul>
            </li>

            <li className="dropdown">
              <button
                onClick={() => handleDropdownClick("kurumsal")}
                className={activeDropdown === "kurumsal" ? "active" : ""}
              >
                Kurumsal <CaretDownIcon className="dropdown-icon" />
              </button>
              <ul className={`dropdown-menu ${activeDropdown === "kurumsal" ? "active" : ""}`}>
                <li><Link to="/meclis-kararlari" onClick={handleLinkClick}>Meclis Kararları</Link></li>
                <li><Link to="/faaliyet-raporlari" onClick={handleLinkClick}>Faaliyet Raporları</Link></li>
                <li><Link to="/meclis-uyeleri" onClick={handleLinkClick}>Meclis Üyeleri</Link></li>
                <li><Link to="/mudurlerimiz" onClick={handleLinkClick}>Müdürlerimiz</Link></li>
                <li><Link to="/organizasyon-semasi" onClick={handleLinkClick}>Organizasyon Şeması</Link></li>
                <li><Link to="/kurumsal-kimlik" onClick={handleLinkClick}>Kurumsal Kimlik</Link></li>
                <li>
                  <Link
                    to="/bilgi-edinme-hakki"
                    onClick={handleLinkClick}
                  >
                    Bilgi Edinme Hakkı
                  </Link>
                </li>
                <li><Link to="/bize-bildir" onClick={handleLinkClick}>Bize Bildir</Link></li>
              </ul>
            </li>

            <li className="dropdown">
              <button
                onClick={() => handleDropdownClick("iskenderun")}
                className={activeDropdown === "iskenderun" ? "active" : ""}
              >
                İskenderun <CaretDownIcon className="dropdown-icon" />
              </button>
              <ul className={`dropdown-menu ${activeDropdown === "iskenderun" ? "active" : ""}`}>
                <li><Link to="/muhtarliklar" onClick={handleLinkClick}>Muhtarlıklar</Link></li>
                <li><Link to="/iskenderun-tarihi" onClick={handleLinkClick}>İskenderun'un Tarihi</Link></li>
                <li><Link to="/iskenderun-sehir-haritasi" onClick={handleLinkClick}>İskenderun Şehir Haritası</Link></li>
                <li><Link to="/projeler" onClick={handleLinkClick}>Projeler</Link></li>
                <li><Link to="/mahalleler" onClick={handleLinkClick}>Mahallem Gelişiyor</Link></li>
                <li><Link to="/sosyal-tesisler" onClick={handleLinkClick}>Sosyal Tesislerimiz</Link></li>
                <li><Link to="/afet-acil-toplanma-alanlari" onClick={handleLinkClick}>Afet Acil Toplanma Alanları</Link></li>
                <li><Link to="/etkinlikler" onClick={handleLinkClick}>Etkinlikler</Link></li>
                <li><Link to="/haberler" onClick={handleLinkClick}>Haberler</Link></li>
                <li><Link to="/videolar" onClick={handleLinkClick}>Video Galeri</Link></li>
              </ul>
            </li>

            <li className="dropdown">
              <button
                onClick={() => handleDropdownClick("hizmet")}
                className={activeDropdown === "hizmet" ? "active" : ""}
              >
                Hizmet Rehberi <CaretDownIcon className="dropdown-icon" />
              </button>
              <ul className={`dropdown-menu ${activeDropdown === "hizmet" ? "active" : ""}`}>
                <li>
                  <a
                    href={siteSettings?.ePayUrl || "https://webportal.iskenderun.bel.tr/engine/saysis.php?szbrtys=wE5JQU"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                  >
                    E-Ödemeler
                  </a>
                </li>
                <li><Link to="/nikah-islemleri" onClick={handleLinkClick}>Nikah İşlemleri</Link></li>
                <li>
                  <Link
                    to={siteSettings?.whiteDeskUrl || "https://webportal.iskenderun.bel.tr/engine/saysis.php?szbrtys=EvWIsV"}
                    onClick={handleLinkClick}
                  >
                    Beyaz Masa
                  </Link>
                </li>
                <li>
                  <a
                    href={siteSettings?.barberUrl || "https://webportal.iskenderun.bel.tr/engine/index.php?param=salon"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                  >
                    Kuaför Randevu Sistemi
                  </a>
                </li>
                {qrModeEnabled && (
                  <li><Link to="/hizmet-rehberi/kutuphane-randevu" onClick={handleLinkClick}>Kütüphane Randevu Sistemi</Link></li>
                )}
                <li><Link to="/hizmet-rehberi/spor-randevu" onClick={handleLinkClick}>Spor Randevu Sistemi</Link></li>
                <li><Link to="/ihaleler" onClick={handleLinkClick}>İhaleler</Link></li>
                <li><Link to="/ilanlar" onClick={handleLinkClick}>İlanlar</Link></li>
                <li><Link to="/duyurular" onClick={handleLinkClick}>Duyurular</Link></li>
                <li>
                  <a
                    href="https://kariyer.iskenderun.bel.tr"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                  >
                    Kariyer Ofisi
                  </a>
                </li>
              </ul>
            </li>

            <li>
              <Link to="/iletisim" onClick={handleLinkClick}>İletişim</Link>
            </li>

            <li className="mobile-ebelediye-link">
              <a
                href={eBelediyeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ebelediye-button"
                onClick={handleLinkClick}
              >
                <span>E-Belediye</span>
                <FaArrowRightLong />
              </a>
            </li>
          </ul>
        </nav>

        <div className="header-actions">
          <form onSubmit={handleSearch} className="header-search-form">
            <input
              type="text"
              placeholder="Ara"
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="search-button" aria-label="Ara">
              <SearchIcon size={18} />
            </button>
          </form>

          <a
            href={eBelediyeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ebelediye-button desktop-ebelediye-button"
          >
            <span>E-Belediye</span>
            <FaArrowRightLong />
          </a>

          <div className="header-meta">
            <div className="topbar-weather">
              {temperature ? `${temperature}°C` : "--°C"}
              <Icon icon={getWeatherIcon(temperature)} width="20" />
            </div>
            <div className="social-icons">
              <a
                href="https://www.facebook.com/iskenderunbel/?locale=tr_TR"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://x.com/iskenderunbel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
              >
                <XIcon />
              </a>
              <a
                href="https://www.instagram.com/iskenderunbel/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://www.youtube.com/@iskenderunbel."
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <YoutubeIcon />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
